import { CircleOff, CirclePlay, Info, Pencil, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { Permission } from '@/features/roles/consts/permissions'
import { DetailFieldRow } from '@/shared/components/detail-rows/detail-field-row'
import { UserChip } from '@/shared/components/detail-rows/user-chip'
import { SidePanelActions } from '@/shared/components/side-panel/side-panel-actions'
import {
  SidePanelSection,
  SidePanelSectionBody
} from '@/shared/components/side-panel/side-panel-blocks'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { useSidePanelStore } from '@/shared/store/side-panel.store'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/shared/ui/accordion'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/shared/ui/alert-dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs'
import { formatHumanDateTime } from '@/shared/utils/format-date'
import {
  useDeleteWorkflowMutation,
  useUpdateWorkflowMutation
} from '../hooks/use-workflow-mutations'
import { LABEL_ACTIONS_WORKFLOW, TYPE_ACTIONS_WORKFLOW } from '../lib/consts'
import type { WorkflowAdminRow } from '../models/workflow-admin'
import { WorkflowForm } from './workflow-form'
import { WorkflowActiveBadge, WorkflowDefaultBadge } from './workflows-admin-columns'

type WorkflowDetailPanelProps = {
  workflow: WorkflowAdminRow
}

const ACTION_LABELS: Record<string, string> = {
  [TYPE_ACTIONS_WORKFLOW.NEW]: LABEL_ACTIONS_WORKFLOW.NEW
}

/**
 * Panel de detalle del flujo de trabajo: modo VISTA (resumen + acciones + tab
 * "Más información" con acordeones de secuencia y actividad) y modo FORMULARIO
 * in-place (mismo WorkflowForm que la creación). Las acciones que el backend
 * rechaza de plano se ocultan: Eliminar para el flujo predeterminado de su
 * acción (responde 400 — hay que asignar otro default primero).
 */
export function WorkflowDetailPanel({ workflow }: WorkflowDetailPanelProps) {
  const [mode, setMode] = useState<'view' | 'edit'>('view')
  const [confirmDelete, setConfirmDelete] = useState(false)
  const closePanel = useSidePanelStore((s) => s.closePanel)
  const updateMutation = useUpdateWorkflowMutation()
  const deleteMutation = useDeleteWorkflowMutation()
  const { has } = usePermissions()

  // El default de una acción no se elimina (el backend responde 400).
  const isDeletable = !workflow.default

  const toggleActive = () => {
    updateMutation.mutate({ id: workflow._id, payload: { active: !workflow.active } })
  }

  // Docs sembrados antes de existir estos campos no los traen — con fallback.
  const steps = workflow.states ?? []
  const actionLabels = workflow.actions.map((action) => ACTION_LABELS[action] ?? action)

  if (mode === 'edit') {
    return (
      <WorkflowForm
        defaultValues={{
          name: workflow.name,
          description: workflow.description ?? '',
          actions: workflow.actions,
          states: steps
            .filter((step) => step.state !== null)
            .map((step) => ({ state: step.state?._id ?? '' })),
          default: workflow.default,
          active: workflow.active
        }}
        lockActions={workflow.default}
        submitLabel="Guardar cambios"
        pending={updateMutation.isPending}
        onSubmit={(values) => {
          updateMutation.mutate(
            { id: workflow._id, payload: values },
            {
              onSuccess: () => {
                setMode('view')
              }
            }
          )
        }}
        onCancel={() => {
          setMode('view')
        }}
      />
    )
  }

  return (
    <div className="flex flex-col">
      <SidePanelSection>
        <SidePanelSectionBody className="gap-3">
          <div className="min-w-0">
            <h3 className="truncate text-base font-semibold leading-tight">{workflow.name}</h3>
            {workflow.description && (
              <p className="text-sm text-muted-foreground">{workflow.description}</p>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <DetailFieldRow
              label="Estado"
              value={<WorkflowActiveBadge active={workflow.active} />}
            />
            <DetailFieldRow label="Acción" value={actionLabels.join(', ') || '—'} />
            <DetailFieldRow
              label="Predeterminado"
              value={workflow.default ? <WorkflowDefaultBadge /> : 'No'}
            />
            <DetailFieldRow label="Estados" value={String(steps.length)} />
          </div>
        </SidePanelSectionBody>
      </SidePanelSection>

      <SidePanelSection>
        <SidePanelActions
          maxInline={2}
          actions={[
            ...(has(Permission.UpdateWorkflows)
              ? [
                  {
                    label: 'Editar flujo',
                    icon: <Pencil className="h-4 w-4" />,
                    variant: 'default' as const,
                    primary: true,
                    onClick: () => {
                      setMode('edit')
                    }
                  },
                  {
                    label: workflow.active ? 'Desactivar flujo' : 'Activar flujo',
                    icon: workflow.active ? (
                      <CircleOff className="h-4 w-4" />
                    ) : (
                      <CirclePlay className="h-4 w-4" />
                    ),
                    variant: 'outline' as const,
                    disabled: updateMutation.isPending,
                    onClick: toggleActive
                  }
                ]
              : []),
            ...(has(Permission.DeleteWorkflows) && isDeletable
              ? [
                  {
                    label: 'Eliminar flujo',
                    icon: <Trash2 className="h-4 w-4" />,
                    variant: 'destructive' as const,
                    disabled: deleteMutation.isPending,
                    onClick: () => {
                      setConfirmDelete(true)
                    }
                  }
                ]
              : [])
          ]}
        />
      </SidePanelSection>

      <Tabs defaultValue="more-info">
        <TabsList className="w-full justify-normal rounded-none">
          <TabsTrigger className="flex gap-1" value="more-info">
            <Info className="size-4" /> Más información
          </TabsTrigger>
        </TabsList>
        <TabsContent value="more-info">
          <Accordion type="multiple" defaultValue={['sequence', 'activity']}>
            <AccordionItem value="sequence">
              <AccordionTrigger className="px-3 border-b border-transparent data-[state=open]:border-border">
                Secuencia de estados ({steps.length})
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-1.5 pt-2 pl-3 pr-2">
                  {steps.length === 0 && (
                    <p className="text-sm text-muted-foreground">
                      Este flujo no tiene estados asignados.
                    </p>
                  )}
                  {steps.map((step) => (
                    <div
                      key={`${step.order}-${step.state?._id ?? 'gone'}`}
                      className="flex items-center gap-2"
                    >
                      <span className="w-5 shrink-0 text-center text-xs font-medium tabular-nums text-muted-foreground">
                        {step.order}
                      </span>
                      <span
                        aria-hidden="true"
                        className="h-1.5 w-1.5 shrink-0 rounded-full"
                        style={{ backgroundColor: step.state?.color ?? '#a3a3a3' }}
                      />
                      <span className="min-w-0 truncate text-sm">
                        {step.state?.name ?? 'Estado eliminado'}
                      </span>
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="activity">
              <AccordionTrigger className="px-3 border-b border-transparent data-[state=open]:border-border">
                Actividad
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-2 pt-2 pl-3 pr-2">
                  <DetailFieldRow label="Creado" value={formatHumanDateTime(workflow.createdAt)} />
                  <DetailFieldRow
                    label="Creado por"
                    value={<UserChip name={workflow.createdBy?.fullname} />}
                  />
                  <DetailFieldRow
                    label="Actualizado"
                    value={formatHumanDateTime(workflow.updatedAt)}
                  />
                  <DetailFieldRow
                    label="Actualizado por"
                    value={<UserChip name={workflow.updatedBy?.fullname} />}
                  />
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </TabsContent>
      </Tabs>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar flujo de trabajo</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará “{workflow.name}”. Las negociaciones que lo referencian conservan su
              historial.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                deleteMutation.mutate(workflow._id, {
                  onSuccess: () => {
                    closePanel()
                  }
                })
              }}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
